import { SkeletonLoader } from "@pepperstone/pepper-ui-kit";
import { useCallback, useEffect, useState } from "react";
import type { CashierTradingAccount } from "@/api/cashier";
import { AccountCard } from "@/components/account-list/AccountCard";
import styles from "./AccountList.module.css";

type AccountListProps = {
  accountList?: CashierTradingAccount[];
  selectedAccountChange?: (account: CashierTradingAccount) => void;
  initialSelectedAccount?: CashierTradingAccount;
  isLoading?: boolean;
  accountDisabled?: (account: CashierTradingAccount) => boolean;
};

export const AccountList = ({
  accountList,
  selectedAccountChange,
  initialSelectedAccount,
  isLoading,
  accountDisabled,
}: AccountListProps) => {
  const [selectedAccount, setSelectedAccount] = useState<
    CashierTradingAccount | undefined
  >();

  const handleSelectAccount = useCallback(
    (selectedAccount: CashierTradingAccount) => {
      setSelectedAccount(selectedAccount);
      selectedAccountChange?.(selectedAccount);
    },
    [selectedAccountChange]
  );

  useEffect(() => {
    if (initialSelectedAccount) {
      const account = accountList?.find(
        (a) => a.id === initialSelectedAccount.id
      );
      handleSelectAccount(account ?? initialSelectedAccount);
      return;
    }
    if (accountList?.[0]) {
      handleSelectAccount(accountList?.[0]);
    }
  }, [accountList, handleSelectAccount, initialSelectedAccount]);

  return (
    <div className={styles.container} data-testid="account-list">
      {isLoading ? (
        <>
          <SkeletonLoader height={16} width={120} variant="rectangular" />
          <SkeletonLoader height={16} width={120} variant="rectangular" />
          <SkeletonLoader height={16} width={120} variant="rectangular" />
          <SkeletonLoader height={16} width={120} variant="rectangular" />
        </>
      ) : (
        accountList?.map((account) => {
          return (
            <AccountCard
              account={account}
              key={account.id}
              onClick={() => handleSelectAccount(account)}
              selected={account.id === selectedAccount?.id}
              disabled={accountDisabled?.(account)}
            />
          );
        })
      )}
    </div>
  );
};
